import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn.ts';

type Variant = 'center' | 'side';

const CONTENT: Record<Variant, string> = {
  // Plein écran sur mobile, centré à partir de 640 px.
  center:
    'fixed inset-0 z-50 flex flex-col bg-white shadow-xl outline-none sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90vh] sm:w-[calc(100%-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl',
  // Panneau latéral à droite (plein écran sur mobile).
  side: 'fixed inset-0 z-50 flex flex-col bg-white shadow-xl outline-none sm:left-auto sm:w-[34rem] sm:max-w-full',
};

const SIZES = { sm: 'sm:max-w-md', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' } as const;

/** Dialogue accessible (Radix) : titre obligatoire, focus piégé, Échap ferme. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  variant = 'center',
  size = 'md',
  closeLabel = 'Fermer',
  testId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  variant?: Variant;
  size?: keyof typeof SIZES;
  closeLabel?: string;
  testId?: string;
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <RadixDialog.Content
          className={cn(CONTENT[variant], variant === 'center' && SIZES[size])}
          data-testid={testId}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
            <div className="min-w-0">
              <RadixDialog.Title className="font-heading text-lg font-semibold text-slate-900">{title}</RadixDialog.Title>
              {description ? <RadixDialog.Description className="mt-0.5 text-sm text-slate-500">{description}</RadixDialog.Description> : null}
            </div>
            <RadixDialog.Close
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              aria-label={closeLabel}
            >
              <X size={18} aria-hidden />
            </RadixDialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer ? <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-5 py-3">{footer}</div> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
